import { useMemo } from 'react';
import { RegisterOptions } from 'react-hook-form';
import { CountrySelector } from '@components/country-selector';
import { GroupRadioSelector } from '@components/group-radio-selector';
import { Input } from '@components/input';
import { InputBankNumber } from '@components/input-bank-number';
import { InputCheckbox } from '@components/input-checkbox';
import { InputGender } from '@components/input-gender';
import { InputMultiEmail } from '@components/input-multi-email';
import { InputPhone } from '@components/input-phone';
import { InputRadio } from '@components/input-radio';
import { InputUrlSimple } from '@components/input-url-simple';
import { InputVatNumber } from '@components/input-vat-number';
import { SelectWithOptions } from '@components/select';
import { SimpleDateInput } from '@components/simple-date-input';
import { Countries } from '@constants/countries';
import { camelize } from '@utils/camel-case-string';
import { type i18n as I18n, type TFunction } from 'i18next';

import { DataType, DynamicInputFields, Field } from './dynamic-input-fields.type';
import {
  getBankValidation,
  getBasicValidationRules,
  getDateValidation,
  getEmailValidation,
  getInstagramValidation,
  getMultiEmailValidation,
  getPhoneValidation,
  getTextValidation,
  getURLValidation,
} from './validations';

export const FIELD_FIRST_NAME = 'firstName';
export const FIELD_LAST_NAME = 'lastName';
export const FIELD_EMAIL = 'email';
export const FIELD_PHONE = 'phone';
export const FIELD_GENDER = 'gender';
export const FIELD_NATIONALITY = 'nationality';
export const FIELD_BIRTH = 'birth';
export const FIELD_RADIO = 'radio';
export const FIELD_CHECKBOX = 'checkbox';
export const FIELD_LIST = 'list';
export const FIELD_NUMERIC_LIST = 'numericlist';
export const FIELD_ID_NUMBER = 'idNumber';
export const FIELD_QUANTITY = 'quantity';
export const FIELD_ROLE_ID = 'roleId';
export const FIELD_IBAN = 'iban';
export const FIELD_MULTI_EMAIL = 'multiEmail';
export const FIELD_VAT = 'vat';
export const REQUIRED_TEXT_ERROR = 'errors:formFieldRequired';

export const DATA_TYPE_TEXT = 'text';
export const DATA_TYPE_EMAIL = 'email';
export const DATA_TYPE_LIST = 'list';

export const FIXED_FIELDS = [
  FIELD_FIRST_NAME,
  FIELD_LAST_NAME,
  FIELD_EMAIL,
  FIELD_PHONE,
  FIELD_GENDER,
  FIELD_NATIONALITY,
  FIELD_BIRTH,
  FIELD_RADIO,
  FIELD_CHECKBOX,
  FIELD_ID_NUMBER,
  FIELD_LIST,
  FIELD_NUMERIC_LIST,
  FIELD_MULTI_EMAIL,
  FIELD_VAT,
];

export const createOptions = ({
  min,
  max,
  options,
}: {
  min?: number;
  max?: number;
  options?: { label: string; value: string }[];
}) => {
  return typeof min === 'number' && typeof max === 'number'
    ? Array.from({ length: max - min + 1 }, (_, i) => (min ?? 0) + i).map((value) => ({
        label: value.toString(),
        value: value.toString(),
      }))
    : options;
};

export const getOptionalLabel = (t: TFunction, isRequired: boolean, optionalFields?: boolean) => {
  if (isRequired) return '';
  return optionalFields ? t('common:optionalLabel') : '';
};

const getLabel = (field: Field, t: TFunction, i18n: I18n) => {
  return i18n.exists(`${field.slug}FormFieldLabel`) ? t(`${field.slug}FormFieldLabel`) : field.name;
};

export const generateLabel = (field: Field, t: TFunction, i18n: I18n, optionalFields?: boolean) => {
  return `
    ${getLabel(field, t, i18n)}
    ${getOptionalLabel(t, field.isRequired, optionalFields)}`;
};

export const getPlaceholderText = (field: Field, t: TFunction, i18n: I18n) => {
  return i18n.exists(`${field.slug}FormFieldPlaceholder`) ? t(`${field.slug}FormFieldPlaceholder`) : field.name;
};

export const getComponentFieldBy = ({
  fieldName,
  venueIsoCode,
  options,
  countries,
  t,
}: {
  fieldName: DataType;
  venueIsoCode?: string;
  options?: { label: string; value: string }[];
  countries?: Countries;
  t?: TFunction;
}) => {
  const fields: DynamicInputFields = {
    gender: {
      name: InputGender,
      extraProps: {
        selectFontLight: true,
      },
    },
    date: {
      name: SimpleDateInput,
      extraProps: {},
    },
    email: {
      name: Input,
      extraProps: {},
    },
    plainText: {
      name: Input,
      extraProps: {},
    },
    text: {
      name: Input,
      extraProps: {},
    },
    radio: {
      name: InputRadio,
      extraProps: {},
    },
    groupRadio: {
      name: GroupRadioSelector,
      extraProps: {
        containerClassName: 'flex-1 p-6 border bg-primary rounded-lg',
        options: options ?? [],
      },
    },
    checkbox: {
      name: InputCheckbox,
      extraProps: {},
    },
    list: {
      name: SelectWithOptions,
      extraProps: { selectFontLight: true, options: options ?? [] },
    },
    numericlist: {
      name: SelectWithOptions,
      extraProps: { selectFontLight: true, options: options ?? [] },
    },
    iban: {
      name: InputBankNumber,
      extraProps: { type: FIELD_IBAN },
    },
    url: {
      name: InputUrlSimple,
      extraProps: {},
    },
    instagram: {
      name: InputUrlSimple,
      extraProps: {},
    },
  };
  if (t) {
    fields.multiEmail = {
      name: InputMultiEmail,
      extraProps: {
        helperText: t('companySettings:multiEmailHintText'),
        optional: true,
        placeholder: t('companySettings:multiEmailPlaceholder'),
      },
    };
    fields.vat = {
      name: InputVatNumber,
      extraProps: {
        countryLabel: t('companySettings:countryCodeLabel'),
        vatLabel: t('companySettings:vatNumberLabel'),
        showWrapperLabel: false,
        showWrapperError: false,
      },
    };
  }
  fields.numeric = {
    name: InputPhone,
    extraProps: {
      showDial: false,
      initialCountry: venueIsoCode ?? '',
      selectFontLight: true,
    },
  };
  fields.country = {
    name: CountrySelector,
    extraProps: {
      showDial: false,
      showSelectedLabel: true,
      initialCountry: venueIsoCode ?? '',
      selectFontLight: true,
      ...(countries && { countries }),
    },
  };

  return fields[fieldName];
};

export const generateRegisterOptions = (field: Field, t: TFunction, optionalFields?: boolean): RegisterOptions => {
  const baseOptions = getBasicValidationRules(field, t, optionalFields);

  switch (field.dataType) {
    case 'email':
      return { ...baseOptions, ...getEmailValidation(t) } as RegisterOptions;
    case FIELD_MULTI_EMAIL:
      return { ...baseOptions, ...getMultiEmailValidation(field, t) } as RegisterOptions;
    case 'text':
      return { ...baseOptions, ...getTextValidation(t) } as RegisterOptions;
    case 'numeric':
      return { ...baseOptions, ...getPhoneValidation(t) } as RegisterOptions;
    case 'date':
      return { ...baseOptions, ...getDateValidation(t) } as RegisterOptions;
    case 'iban':
    case 'vat':
      return { ...baseOptions, ...getBankValidation(field.dataType, t) } as RegisterOptions;
    case 'url':
      return { ...baseOptions, ...getURLValidation(t) } as RegisterOptions;
    case 'instagram':
      return { ...baseOptions, ...getInstagramValidation(t) } as RegisterOptions;
    default:
      return baseOptions;
  }
};

export const stableSort = <T>(array: T[], compare: (a: T, b: T) => number): T[] => {
  const stabilizedArray = array.map((el, index) => [el, index] as [T, number]);
  stabilizedArray.sort((a, b) => {
    const order = compare(a[0], b[0]);
    return order === 0 ? a[1] - b[1] : order;
  });
  return stabilizedArray.map((el) => el[0]);
};

export const sortFixedFields = <T extends { name: string }>(a: T, b: T) => {
  const indexA = FIXED_FIELDS.indexOf(camelize(a.name.toLowerCase()));
  const indexB = FIXED_FIELDS.indexOf(camelize(b.name.toLowerCase()));

  if (indexA === -1 && indexB === -1) return 0;
  if (indexA === -1) return 1;
  if (indexB === -1) return -1;
  return indexA - indexB;
};

export const useSortedFields = <T extends { name: string; id: string }>(fields: T[], indexes: Map<string, number>) => {
  return useMemo(() => {
    return stableSort(fields, sortFixedFields).map((field) => ({
      ...field,
      index: indexes.get(field.id),
    }));
  }, [fields, indexes]);
};
